'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

/**
 * 別ページへ移動したらスクロールを先頭に戻す。
 *
 * 本来 App Router が自前でやる処理だが、loading.tsx を置いたセグメントでは動かない。Next はスクロール先の
 * 要素を ReactDOM.findDOMNode で決めており、loading.tsx があるとフォールバックの先頭が骨組みではなく
 * loading.css の <link>（head 内）になる。矩形が全部ゼロなので読み飛ばし、そのまま head を辿り切って
 * 中断する。骨組みの高さや構造を変えても head の中で終わるため回避できない（next 14.2.35 で確認）。
 * @see https://github.com/vercel/next.js/issues/74485
 *
 * loading.tsx が無いルートでも、Next が戻すのは実コンテンツが届いた後（実測 0.8〜1.3 秒）。それまでは
 * 前のページのスクロール位置が残り、フォールバックが短いとブラウザが位置を最下部へ切り詰めるため、
 * 読み込み中ずっとフッターが画面いっぱいに映る。パスが変わった時点で戻せば、どちらの場合も先頭から見える。
 *
 * 動かすのは「移動した後」に限る。リンク押下時など移動前に動かすと、離脱するページのスクロール位置が
 * 先頭として履歴に残り、戻ったときに元の位置へ復元されなくなる。
 *
 * behavior: 'instant' は global.scss の `html { scroll-behavior: smooth }` を打ち消すために指定する。
 */
function ScrollResetOnNavigation() {
  const pathname = usePathname();
  const previousPathnameRef = useRef(pathname);
  // 戻る/進むで到達したパス。「戻った」という事実だけを持つと、ページ内リンク（href="#..."）から
  // 戻ったときのようにパスが変わらない履歴移動で印が残り、次の通常遷移を取りこぼす
  const historyNavigationPathnameRef = useRef<string | null>(null);

  useEffect(() => {
    const markHistoryNavigation = () => {
      historyNavigationPathnameRef.current = window.location.pathname;
    };
    window.addEventListener('popstate', markHistoryNavigation);
    return () => window.removeEventListener('popstate', markHistoryNavigation);
  }, []);

  useEffect(() => {
    if (previousPathnameRef.current === pathname) return;
    previousPathnameRef.current = pathname;

    // 戻る/進むはブラウザが元の位置を復元するので邪魔しない。
    // 一致しないときは印を残す。遷移中に戻った場合、先に別のパスがコミットされても復元を守れる
    if (historyNavigationPathnameRef.current === pathname) {
      historyNavigationPathnameRef.current = null;
      return;
    }

    // 位置を指定して飛ぶリンク（/foo#bar）は、その位置へ寄せる Next の処理を上書きしない
    if (window.location.hash) return;

    window.scrollTo({ top: 0, behavior: 'instant' });
    // モーダルから離脱した直後は body のスクロールロック解除が間に合わず、SP でこの scrollTo が
    // 効かないことがある（ProductOptionContainer が rAF を挟んでいるのと同じ理由）。次のフレームで戻し直す
    const retryId = requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'instant' });
    });
    return () => cancelAnimationFrame(retryId);
  }, [pathname]);

  return null;
}

export default ScrollResetOnNavigation;
